import { memo } from 'react';
import { Typography, Empty, Spin, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';

import { Question } from './QuestionsList';
import Rate from './Charts/Rate';
import Choice from './Charts/Choice';
import NPS from './Charts/NPS';
import Scale from './Charts/Scale';
import Input from './Charts/Input';
import { isInRole } from '../../auth/components/RenderForRole';
import styles from './report.module.css';
import { ReportProps } from './Report';
import SurveysService from '../SurveysService';
import { useQuery } from '@tanstack/react-query';

type ReportQuestionProps = Omit<ReportProps, 'questions'> & {
  question: Question;
  index: number;
  shouldLoad: boolean;
  onDataLoaded: (index: number) => void;
};

const ReportQuestion = ({
  question,
  client,
  surveyId,
  filters,
  index,
  shouldLoad,
  onDataLoaded,
}: ReportQuestionProps) => {
  //const { ref, inView } = useInView({ triggerOnce: true, initialInView: index < 3,  rootMargin: '-50px' });

  const queryData = async () => {
    let data = await SurveysService.getQuestionResponses(
      client,
      surveyId,
      question.id,
      filters
    );
    return data;
  };

  const { isLoading, data: response } = useQuery(
    ['question_results', question.id, filters],
    queryData,
    {
      enabled: shouldLoad,
      onSettled: () => onDataLoaded(index),
      onError: (err) => {
        console.error(err);
        message.error((err as any).message);
      },
    }
  );

  let inner = <div />;

  //if(inView) {
  if (
    question.type === 'Note' ||
    question.type === 'ThankYou' ||
    question.hide_result
  ) {
    return <div key={question.id} />;
  } else if (isLoading) {
    inner = (
      <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
    );
  } else if (
    response?.total_range === 0 ||
    question.type === 'Note' ||
    question.type === 'ThankYou'
  ) {
    inner = (
      <Empty description='No hay información para la pregunta en el momento' />
    );
  } else if (question.sensible_data && !isInRole(['admin', 'superadmin'])) {
    inner = (
      <Empty description={`${response?.total_range} respuestas ocultas`} />
    );
  } else if (question.type === 'ScaleGroup') {
    inner = <div className={styles.group} />;
  } else if (question.type === 'Rate') {
    inner = <Rate data={response} question={question} />;
  } else if (question.type === 'Scale' && question.subtype === 'NPS') {
    inner = <NPS data={response} />;
  } else if (question.type === 'Scale') {
    inner = <Scale data={response} question={question} />;
  } else if (question.type === 'Choice' || question.type === 'YesNo') {
    inner = <Choice data={response} question={question} />;
  } else if (question.type === 'Input') {
    inner = (
      <Input
        data={response}
        question={question}
        client={client}
        surveyId={surveyId}
        filters
      />
    );
  }
  //}

  return (
    <div
      key={question.id}
      id={`question_${question.id}`}
      className={styles.question}
    >
      <div className={styles.titleBar}>
        <div className={styles.title}>
          <Typography.Title level={4}>
            {
              <ReactMarkdown
                components={{
                  a: (props) => (
                    <a
                      href={props.href}
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      {props.children}
                    </a>
                  ),
                }}
              >
                {question.title}
              </ReactMarkdown>
            }
          </Typography.Title>
          <Typography.Text>
            {
              <ReactMarkdown
                components={{
                  a: (props) => (
                    <a
                      href={props.href}
                      rel='noopener noreferrer'
                      target='_blank'
                    >
                      {props.children}
                    </a>
                  ),
                }}
              >
                {question.description || ''}
              </ReactMarkdown>
            }
          </Typography.Text>
        </div>
      </div>
      <div className={styles.chartContainer}>{inner}</div>
    </div>
  );
};

export default memo(ReportQuestion);
